import { useState } from 'react';
import { Checkbox } from '@carbon/react';
import moment from 'moment';
import {
  TextField,
  TextAreaField,
  TanstackFieldWrapper,
} from 'components/form-fields-2';
import { DatePicker } from 'components/date-picker/date-picker';
import { ScenarioDetailsSection } from 'components/scenario-details-section';
import type { ScenarioGlobalConfigsProps } from './types';
import styles from './scenario-global-configs.module.scss';

/**
 * Shared "Scenario Details" panel for all scenario forms.
 * Renders name (required), effective date, optional expiration date,
 * and description inside ScenarioDetailsSection with geometry priority.
 * Accepts children for scenario-specific extra fields (e.g., Tax global rates).
 * @param {ScenarioGlobalConfigsProps} props - The component props
 * @return {JSX.Element} The global configs form
 */
export function ScenarioGlobalConfigs({
  form,
  showExpirationDate = false,
  namePlaceholder = 'e.g. Permian Basin Acquisition — 2026',
  descriptionPlaceholder = 'Describe the purpose and scope of this scenario...',
  children,
}: ScenarioGlobalConfigsProps): JSX.Element {
  const [expirationEnabled, setExpirationEnabled] = useState(() => {
    try {
      return !!form.getFieldValue('global.expirationDate');
    } catch {
      return false;
    }
  });

  return (
    <form.Subscribe
      selector={(state: any) => ({
        showWarning: !state.values.global.name.trim(),
        priorityItems: state.values.global.geometryPriority,
      })}
    >
      {({
        showWarning,
        priorityItems,
      }: {
        showWarning: boolean;
        priorityItems: string[];
      }) => (
        <ScenarioDetailsSection
          geometryPriorityItems={priorityItems}
          onGeometryPriorityChange={(items) =>
            form.setFieldValue('global.geometryPriority', items)
          }
          showNameWarning={showWarning}
        >
          <div className={styles.fields}>
            <div className={styles.nameRow}>
              <form.Field
                name="global.name"
                validators={{
                  onChange: ({ value }: { value: string }) => {
                    return value.trim() ? undefined : 'This field is required';
                  },
                }}
              >
                {(field: any) => (
                  <TanstackFieldWrapper<string> field={field}>
                    {(props: any) => (
                      <TextField
                        id="scenario-name"
                        labelText="Scenario Name (required)"
                        placeholder={namePlaceholder}
                        {...props}
                      />
                    )}
                  </TanstackFieldWrapper>
                )}
              </form.Field>

              <form.Field name="global.effectiveDate">
                {(field: any) => (
                  <DatePicker
                    id="effective-date"
                    labelText="Effective Date"
                    value={field.state.value || undefined}
                    light
                    onChange={(dates: Date[]) => {
                      if (dates[0]) {
                        field.handleChange(
                          moment(dates[0]).format('YYYY-MM-DD')
                        );
                      } else {
                        field.handleChange('');
                      }
                    }}
                  />
                )}
              </form.Field>
            </div>

            {showExpirationDate && (
              <div className={styles.expirationRow}>
                <Checkbox
                  id="set-expiration-date"
                  labelText="Set expiration date"
                  checked={expirationEnabled}
                  onChange={(_: any, { checked }: { checked: boolean }) => {
                    setExpirationEnabled(checked);
                    if (!checked) {
                      form.setFieldValue('global.expirationDate', null);
                    }
                  }}
                />
                {expirationEnabled && (
                  <form.Field name="global.expirationDate">
                    {(field: any) => (
                      <DatePicker
                        id="expiration-date"
                        labelText="Expiration Date"
                        value={field.state.value || undefined}
                        light
                        onChange={(dates: Date[]) => {
                          if (dates[0]) {
                            field.handleChange(
                              moment(dates[0]).format('YYYY-MM-DD')
                            );
                          } else {
                            field.handleChange('');
                          }
                        }}
                      />
                    )}
                  </form.Field>
                )}
              </div>
            )}

            <form.Field name="global.description">
              {(field: any) => (
                <TanstackFieldWrapper<string> field={field}>
                  {(props: any) => (
                    <TextAreaField
                      id="scenario-description"
                      labelText="Description"
                      placeholder={descriptionPlaceholder}
                      rows={6}
                      {...props}
                    />
                  )}
                </TanstackFieldWrapper>
              )}
            </form.Field>

            {children}
          </div>
        </ScenarioDetailsSection>
      )}
    </form.Subscribe>
  );
}
